import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './ECGGraphPlot.css'; // Optional: for styling
import axios from 'axios';
import { backendlink } from '../../config';
import { LoadIndicator } from 'devextreme-react';

const ECGGraphPlot = ({ ECGDataID, patientToken, user }) => {
  const svgRef = useRef();
  const [ecgDataInfo, setecgDataInfo] = useState([]);
  const [loading, setLoading] = useState(false); // State to track loading status

  const formatUnixTime = (unixTime) => {
    const date = new Date(unixTime * 1000); // Convert Unix time to milliseconds
    const hours = String(date.getHours()).padStart(2, '0'); // Get local hours
    const minutes = String(date.getMinutes()).padStart(2, '0'); // Get local minutes
    const seconds = String(date.getSeconds()).padStart(2, '0'); // Get local seconds
    return `${hours}:${minutes}:${seconds}`; // Format as hh:mm:ss
  };

  const drawChart = (ecgData) => {
    const mmToPx = 4.63;
    const mmToPxY = 1.20;
    const squareSizePx = 5 * mmToPx;
    const squareSizePxY = 5 * mmToPxY;
    const squareTime = 0.2;
    const squareMv = 0.1;
    const segmentDuration = 10;
    const segments = 3;
    const minMv = -3;
    const maxMv = 3;
    const totalSquaresY = (maxMv - minMv) / squareMv;
    const margin = { top: 20, right: 30, bottom: 40, left: 60 };
    const segmentWidth = (segmentDuration / squareTime) * squareSizePx;
    const segmentHeight = totalSquaresY * squareSizePxY;
    const svgWidth = segmentWidth + margin.left + margin.right;
    const svgHeight = segmentHeight * segments;

    const svg = d3.select(svgRef.current)
      .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');

    const x = d3.scaleLinear()
      .domain([0, segmentDuration])
      .range([margin.left, margin.left + segmentWidth]);

    const y = d3.scaleLinear()
      .domain([minMv, maxMv])
      .range([segmentHeight - margin.bottom, margin.top]);

    const minorInterval = 0.04;

    const line = d3.line()
      .defined(d => d.value !== null)
      .x(d => x(d.time % segmentDuration))
      .y(d => y(d.value));

    svg.selectAll('*').remove(); // Clear previous render

    for (let s = 0; s < segments; s++) {
      const group = svg.append('g')
        .attr('transform', `translate(0, ${s * segmentHeight})`);


      const totalMinorTicks = segmentDuration / minorInterval;
      for (let i = 0; i <= totalMinorTicks; i++) {
        const t = i * minorInterval;
        group.append('line')
          .attr('x1', x(t))
          .attr('y1', margin.top)
          .attr('x2', x(t))
          .attr('y2', segmentHeight - margin.bottom)
          .attr('class', i % 5 === 0 ? 'grid-thick' : 'grid-thin');
      }

      for (let i = 0; i <= totalSquaresY; i++) {
        const v = minMv + i * squareMv;
        group.append('line')
          .attr('x1', margin.left)
          .attr('y1', y(v))
          .attr('x2', margin.left + segmentWidth)
          .attr('y2', y(v))
          .attr('class', i % 5 === 0 ? 'grid-thick' : 'grid-thin');
      }

      const xOffset = s * segmentDuration;
      const xAxis = d3.axisBottom(x)
        .tickValues(d3.range(0, segmentDuration + 1))
        .tickFormat(d => Math.floor(d + xOffset));

      const yAxis = d3.axisLeft(y)
        .tickValues(d3.range(minMv, maxMv + 0.1, 0.5))
        .tickFormat(d => d.toFixed(1));

      group.append('g')
        .attr('transform', `translate(0,${segmentHeight - margin.bottom})`)
        .call(xAxis);

      group.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(yAxis);

      group.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -segmentHeight / 2)
        .attr('y', 15)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .style("font-family", "'Open Sans', sans-serif")
        .text('Voltage (mV)');

      group.append('text')
        .attr('x', margin.left + segmentWidth / 2)
        .attr('y', segmentHeight - 5)
        .attr('text-anchor', 'middle')
        .style("font-family", "'Open Sans', sans-serif")
        .text('Time (Second)');

      const segmentData = ecgData.filter(d => d.time >= s * segmentDuration && d.time < (s + 1) * segmentDuration);

      svg.append("clipPath")
        .attr("id", "clip")
        .append("rect")
        .attr("x", margin.left)
        .attr("y", margin.top)
        .attr("width", segmentWidth)
        .attr("height", segmentHeight - margin.top - margin.bottom);

      group.append("path")
        .datum(segmentData)
        .attr("class", "line")
        .attr("clip-path", "url(#clip)")
        .attr("d", line);



    }
  }

  const renderingData = async () => {
    setLoading(true); // Set loading to true before fetching data
    try {
      const response = await axios.post(backendlink + 'doctor/getpatientecglinedata', {
        patienttoken: patientToken,
        ecgdataid: ECGDataID
      }, {
        headers: {
          token: user.token,
        },
      });
      console.log("Drawing chart for ECGDataID:", response);

      setecgDataInfo(response.data.result[0]);
      const ecgData = response.data.result[0].arrECGdata.map((value, index) => ({
        time: index / 250,
        value: value
      }));

      drawChart(ecgData);
    } catch (error) {
      console.error("Error creating SciChartSurface:", error);
    }
    finally {
      setLoading(false); // Set loading to false after fetching data
    }
  };

  useEffect(() => {
    if (ECGDataID) {
      // drawChart([]);
      renderingData();
    }
  }, [ECGDataID]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', marginLeft: '10px' }}>
      <div className="titles" style={{ textAlign: 'left', width: '100%' }}>
        Selected ECG Graph
      </div>
      <table className='custom-table' style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', textAlign: 'left' }}>
        <thead>
          <tr>
            <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>Time:</th>
            <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>
              {loading ? '' : `${formatUnixTime(ecgDataInfo.start)} - ${formatUnixTime(ecgDataInfo.end)}`}
            </td>
            <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>ECG Mode:</th>
            <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>{loading ? '' : `${ecgDataInfo.ecgMode}`}</td>
          </tr>
          <tr>
            <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>Average Heart Rate:</th>
            <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>{loading ? '' : `${Math.floor(ecgDataInfo.averageBpm)} BPM`}</td>

            <th className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>AF Probability:</th>
            <td className='text-bold-medium' style={{ border: '1px solid #ddd', padding: '8px' }}>{loading ? '' : `${ecgDataInfo.afProb}`}</td>
          </tr>
        </thead>
      </table>
      <div className="scroll-container">
        {loading &&
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px', // Adjust height to center vertically
              width: '100%', // Full width
            }}
          >
            <LoadIndicator
              width={50}
              height={50}
            />
          </div>}
        <svg ref={svgRef}
          style={{ display: loading ? 'none' : 'block' }} // Hide SVG when loading
        ></svg>
      </div>
    </div>

  );
};

export default ECGGraphPlot;
